import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Papa from "papaparse";

import { useUploadFinanceDocumentMutation } from "../../../../store/Company/companyApiSlice";
import { CommonToast } from "../../../common/Toast";
import {
  REQUIRED_CSV_HEADERS,
  matchCsvHeaders,
  validateCsvRows,
} from "./financialSetupFields";

/* Method 3 — fill in the template and upload it in one go. */
export const useFinancialCsv = ({ maxYear, onDone } = {}) => {
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);
  const companyID = currentEditingCompany?.companyID;

  const [uploadFinanceDocument] = useUploadFinanceDocumentMutation();

  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [missingHeaders, setMissingHeaders] = useState([]);
  const [error, setError] = useState("");
  const [parsing, setParsing] = useState(false);
  const [busy, setBusy] = useState(false);

  /* Parsed as soon as a file is chosen, so the columns are checked before the
     viewer presses anything. */
  useEffect(() => {
    if (!file) {
      setRows([]);
      setMissingHeaders([]);
      setError("");
      return;
    }

    setParsing(true);
    Papa.parse(file, {
      header: true,
      transformHeader: (header) => header.trim(),
      dynamicTyping: true,
      skipEmptyLines: true,
      complete: (result) => {
        setParsing(false);
        const parsed = result?.data || [];
        const headers = Object.keys(parsed.length > 0 ? parsed[0] : {});
        const { missing } = matchCsvHeaders(headers);

        setMissingHeaders(missing);
        if (missing.length > 0) {
          setRows([]);
          setError(
            `This file is missing ${missing.length} of the ${REQUIRED_CSV_HEADERS.length} required columns. Use the template to be sure of the names.`
          );
          return;
        }

        setError("");
        setRows(parsed);
      },
      error: () => {
        /* The live parse has no error handler at all, so an unreadable file
           left the button enabled and the screen silent. */
        setParsing(false);
        setRows([]);
        setError("Couldn't read that file. Please check it is a valid CSV.");
      },
    });
  }, [file]);

  const clear = () => {
    setFile(null);
    setRows([]);
    setMissingHeaders([]);
    setError("");
  };

  const save = async () => {
    if (!file) {
      setError("Choose a CSV file to upload.");
      return false;
    }
    if (missingHeaders.length > 0) return false;

    /* Transcribed year rules: no duplicates, three consecutive years from the
       newest, nothing five or more years older. */
    const checked = validateCsvRows(rows, { maxYear });
    if (checked.error) {
      setError(checked.error);
      return false;
    }
    setError("");

    setBusy(true);
    try {
      const res = await uploadFinanceDocument({
        data: checked.rows,
        token,
        company_id: companyID,
      }).unwrap();

      /* Transcribed success test. */
      if (res?.statusCode === 201) {
        CommonToast.success(res?.message || "Financial figures uploaded.");
        onDone?.();
        return true;
      }
      CommonToast.error(res?.message || "Something Went wrong!");
      return false;
    } catch (err) {
      /* The live catch toasts the error OBJECT, which renders as
         "[object Object]". */
      CommonToast.error(
        err?.data?.detail ||
          err?.data?.message ||
          err?.message ||
          "Couldn't upload that file. Please try again."
      );
      return false;
    } finally {
      setBusy(false);
    }
  };

  return {
    file,
    setFile,
    clear,
    rows,
    missingHeaders,
    parsing,
    busy,
    error,
    save,
    ready: Boolean(file) && missingHeaders.length === 0 && !parsing,
  };
};

export default useFinancialCsv;
